import { createDecorator } from "../../instantiation/common/registry.js";
import { IUndoRedoElement } from "./action.js";
import { Event } from "../../../base/common/events.js";

export type UndoOrigin = "local-user" | "ai" | "remote";

export interface UndoProvenance {
    readonly origin: UndoOrigin;
    readonly sessionId?: string;
    readonly actorId?: string;
}

let undoGroupId = 0;
export class UndoGroup {
    readonly id = ++undoGroupId;
    constructor(readonly label: string, readonly code: string = "unknown") {}
}

let undoSourceId = 0;
export class UndoSource {
    readonly id = ++undoSourceId;
    constructor(readonly provenance: UndoProvenance) {}
}

export interface UndoPushOptions {
    readonly group?: UndoGroup;
    readonly source?: UndoSource;
}

export interface IUndoRedoService {
    readonly onDidChange: Event<void>;

    /**
     * Fires when an element PERMANENTLY leaves the history (cleared, evicted past the
     * stack limit, redo invalidated by a new action, or service disposal) — the moment
     * anything the entry retained becomes truly unreachable. Consumers that hold
     * resources hostage to undo (a deleted node → its open file) release them here.
     * Group members are announced individually.
     */
    readonly onDidDisposeElement: Event<{ element: IUndoRedoElement }>;

    push(action: IUndoRedoElement, options?: UndoPushOptions): void,
    beginGroup(label: string, code?: string): UndoGroup,
    endGroup(group: UndoGroup): void,
    createSource(provenance: UndoProvenance): UndoSource,
    /** actions pushed within this window (ms) collapse into ONE undo entry — a
     * user gesture often lands as several pushes. 0 DISABLES grouping (every push
     * is its own entry) — for tests and scripted sequences, where back-to-back
     * commands are separate gestures, not one (Marcel 2026-08-04). */
    groupingDelay: number,
    get canUndo(): boolean,
    get canRedo(): boolean,
    undo(): Promise<void> | void,
    redo(): Promise<void> | void,
    clear(): void,
}

export const IUndoRedoService = createDecorator<IUndoRedoService>('undoRedoService');




